<template>
    <Dialog
        :value="true"
        :header-text="$t('Invoice seller')"
        persistent
        :width="800"
        @close_modal="$emit('close')"
    >
        <v-row v-if="invoiceSellerCopy" class="px-4 pb-4">
            <!-- Line 1 -->
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.name"
                    :label="$t('Company name')"
                    :error-messages="getErrors('name')"
                    @input="setFieldValue('name', $event)"
                />
            </v-col>

            <!-- Line 2 -->
            <v-col class="pb-2" cols="12">
                <text-field
                    :value="invoiceSellerCopy.address"
                    :label="$t('Address')"
                    @input="invoiceSellerCopy.address = $event"
                />
            </v-col>

            <!-- Line 3 -->
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.company_number"
                    :label="$t('Company number')"
                    @input="invoiceSellerCopy.company_number = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.vat_number"
                    :label="$t('Tax number')"
                    @input="invoiceSellerCopy.vat_number = $event"
                />
            </v-col>

            <!-- Line 4 -->
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.bank_1"
                    :label="$t('Bank') + ' 1'"
                    @input="invoiceSellerCopy.bank_1 = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.iban_1"
                    :label="$t('IBAN') + ' 1'"
                    @input="invoiceSellerCopy.iban_1 = $event"
                />
            </v-col>

            <!-- Line 5 -->
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.bank_2"
                    :label="$t('Bank') + ' 2'"
                    @input="invoiceSellerCopy.bank_2 = $event"
                />
            </v-col>
            <v-col class="pb-2" cols="6">
                <text-field
                    :value="invoiceSellerCopy.iban_2"
                    :label="$t('IBAN') + ' 2'"
                    @input="invoiceSellerCopy.iban_2 = $event"
                />
            </v-col>

            <!-- Line 6 -->
            <v-col cols="6">
                <v-row v-if="invoiceSellerCopy.logo_url">
                    <v-col cols="auto" class="py-0 pr-0">
                        <v-img
                            contain
                            height="60"
                            max-width="180"
                            min-width="60"
                            :src="invoiceSellerCopy.logo_url"
                        >
                        </v-img>
                    </v-col>
                    <v-col cols="auto" class="pa-0">
                        <v-btn
                            class="text-lowercase-first-letter text-none"
                            text
                            small
                            color="error"
                            @click="removeLogo()"
                        >
                            {{ $t('Delete logo') }}
                        </v-btn>
                    </v-col>
                </v-row>
                <FileUploadArea
                    v-else
                    :button-text="$t('Upload logo file')"
                    :file-upload="invoiceSellerLogoUpload"
                    :upload-change="uploadChange"
                    :upload-drop="uploadDrop"
                >
                    <template #help>
                        <div class="body-2 mt-2 font-weight-medium">
                            {{ invoiceSellerCopy.logo_url }}
                            {{ $t("Max. dimensions 130 x 130 px") }}
                        </div>
                    </template>
                </FileUploadArea>
            </v-col>

        </v-row>

        <template slot="footer" v-if="invoiceSellerCopy">
            <danger-button
                :hidden="!invoiceSellerCopy.id || invoiceSellerCopy.status === 'active'"
                @click="onRestoreInvoiceSeller()">
                {{ $t("Restore") }}
            </danger-button>
            <danger-button
                :hidden="!invoiceSellerCopy.id || invoiceSellerCopy.status === 'inactive'"
                @click="onArchiveInvoiceSeller()">
                {{ $t("Archive") }}
            </danger-button>
            <v-spacer/>
            <secondary-button @click="$emit('close')">
                {{ $t("Cancel") }}
            </secondary-button>
            <primary-button @click="onSaveInvoiceSeller">
                {{ $t("Save") }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import Dialog from "../Dialog.vue";
import {ref, onMounted} from "@vue/composition-api";
import {actions as dialogModule} from "../../store/modules/dialog";
import {$t} from "@/i18n";
import ApiInvoiceSeller from "../../api/billing/invoice_seller";
import errorDialog from "@/views/ErrorDialog/errorDialog";
import FileUploadArea from "../CustomField/FileUploadArea.vue";
import FileUpload from "../../composables/FileUpload";
import appStoreDialog from "../../store/modules/AppStoreDialog/appStoreDialog";

export default {
    name: "InvoiceSellerDialog",
    components: {
        FileUploadArea,
        Dialog
    },
    props: {
        companyId: {
            type: Number,
            required: true
        },
        invoiceSeller: {
            type: Object,
            required: false
        },
    },
    setup(props, ctx) {
        const invoiceSellerCopy = ref(null)
        const errors = ref([])

        const {appStoreDialogState} = appStoreDialog()

        const setInvoiceSellerCopy = () => {
            if (props.invoiceSeller) {
                invoiceSellerCopy.value = props.invoiceSeller
            } else {
                invoiceSellerCopy.value = {
                    id: null,
                    type: 'invoice_seller',
                    name: '',
                    address: '',
                    company_number: '',
                    vat_number: '',
                    bank_1: '',
                    iban_1: '',
                    bank_2: '',
                    iban_2: '',
                    logo_url: '',
                }
            }
        }

        const onArchiveInvoiceSeller = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Archive"),
                cancel_text: $t("Cancel"),
                action: () => archiveInvoiceSeller(),
            })
            dialogModule.openConfirmDialog()
        }
        const archiveInvoiceSeller = async () => {
            try {
                await ApiInvoiceSeller.archiveInvoiceSeller(props.companyId, invoiceSellerCopy.value.id)
                closeDialog()
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const closeDialog = () => {
            errors.value = {}
            invoiceSellerCopy.value = null
            ctx.emit('close')
        }

        const onRestoreInvoiceSeller = () => {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure?"),
                confirm_text: $t("Restore"),
                cancel_text: $t("Cancel"),
                action: () => restoreInvoiceSeller(),
            })
            dialogModule.openConfirmDialog()
        }

        const restoreInvoiceSeller = async () => {
            try {
                await ApiInvoiceSeller.restoreInvoiceSeller(props.companyId, invoiceSellerCopy.value.id)
                closeDialog()
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onSaveInvoiceSeller = async () => {
            try {
                await ApiInvoiceSeller.saveInvoiceSeller(props.companyId, invoiceSellerCopy.value)
                closeDialog()
            } catch (error) {
                if (error.response?.data?.error_type === "access") {
                    errorDialog.showError(error)
                } else {
                    errors.value = error.response.data.errors
                }
            }
        }

        const getErrors = (field) => {
            const paramErrors = []
            for (const error of errors.value) {
                if (error.field === field) {
                    paramErrors.push(error.error_message)
                }
            }
            return paramErrors
        }

        const setFieldValue = (field, value) => {
            invoiceSellerCopy.value[field] = value
            errors.value = errors.value.filter(err => err.field !== field)
        }

        // Logo upload start
        const relatedObject = ref({
            account_id: appStoreDialogState.appData.company_id,
            feature_id: appStoreDialogState.appData.feature_id,
        })

        const uploadData = ref({
            files: []
        })

        const removeLogo = () => {
            invoiceSellerCopy.value.logo_url = null
        }
        const addLogo = (file) => {
            invoiceSellerCopy.value.logo_url = file.temp_name
        }

        const invoiceSellerLogoUpload = FileUpload(
            "invoice_seller_logo", // related_type
            relatedObject, // related_object
            uploadData, // form_data
            false, // isWsUpdate
            addLogo, // file_uploaded
            removeLogo, // file_deleted
            false, // multipleFilesAllowed
            false, // saveTemporary
        );

        const uploadChange = async (e) => {
            invoiceSellerLogoUpload.upload_change(e)
        }

        const uploadDrop = async function (e) {
            e.preventDefault();
            const files = [...e.dataTransfer.items].map(item => item.getAsFile());
            invoiceSellerLogoUpload.upload_drop({
                dataTransfer: {
                    files: files
                }
            })
        };
        // Logo upload end

        onMounted(() => {
            setInvoiceSellerCopy()
        })

        return {
            onArchiveInvoiceSeller,
            onRestoreInvoiceSeller,
            onSaveInvoiceSeller,
            invoiceSellerCopy,
            uploadChange,
            uploadDrop,
            invoiceSellerLogoUpload,
            removeLogo,
            getErrors,
            setFieldValue,
        }
    }
}
</script>

<style scoped lang="scss"></style>